import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, telLink, mailLink, whatsappLink } from "../lib/site";
import { SERVICES } from "../data/services";

const COMPANY = [
  { to: "/about", label: "About Us" },
  { to: "/industries", label: "Industries We Serve" },
  { to: "/insights", label: "Legal Insights" },
  { to: "/contact", label: "Contact & Enquiry" },
];

const LEGAL = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-use", label: "Terms of Use" },
  { to: "/disclaimer", label: "Disclaimer" },
];

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-navy-dark text-white" data-testid="site-footer">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      <div className="container-x grid gap-14 py-20 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-4">
          <Logo light />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
            A legal advisory practice serving businesses, startups and
            individuals across India — corporate and commercial law, trademarks,
            contracts, property, compliance and disputes.
          </p>
          <p className="mt-6 border-l-2 border-gold/60 pl-4 text-xs leading-relaxed text-white/45">
            In accordance with the rules of the Bar Council of India, this
            website is intended solely to provide general information about the
            practice. It is not advertising or solicitation, and its content is
            not legal advice.
          </p>
        </div>

        <nav className="lg:col-span-2" aria-label="Services">
          <p className="eyebrow-on-dark !text-white/40">Services</p>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-sm text-white/65 transition-colors hover:text-gold"
                >
                  {s.shortTitle}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="lg:col-span-2" aria-label="Company and legal">
          <p className="eyebrow-on-dark !text-white/40">Practice</p>
          <ul className="mt-5 space-y-3">
            {COMPANY.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-white/65 transition-colors hover:text-gold"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            {LEGAL.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-xs text-white/40 transition-colors hover:text-gold"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="lg:col-span-4">
          <p className="eyebrow-on-dark !text-white/40">Contact</p>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a
                href={telLink}
                data-testid="footer-phone-link"
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-gold"
              >
                <Phone size={15} className="text-gold" /> {SITE.phoneDisplay}
              </a>
            </li>
            <li>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-gold"
              >
                <svg viewBox="0 0 24 24" className="h-[15px] w-[15px] fill-gold" aria-hidden="true">
                  <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm0 18.12c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.25.25-.85.83-.85 2.02 0 1.19.87 2.34 1 2.5.12.17 1.71 2.61 4.14 3.66.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.12-.22-.18-.47-.31z" />
                </svg>
                WhatsApp enquiry
              </a>
            </li>
            <li>
              <a
                href={mailLink}
                data-testid="footer-email-link"
                className="flex items-center gap-3 text-white/70 transition-colors hover:text-gold"
              >
                <Mail size={15} className="text-gold" /> {SITE.email}
              </a>
            </li>
            <li className="flex gap-3 text-white/50">
              <MapPin size={15} className="mt-0.5 shrink-0 text-gold" />
              <span className="space-y-3">
                {SITE.offices.map((o) => (
                  <span key={o.label} className="block">
                    <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-gold/80">
                      {o.label}
                    </span>
                    {o.lines.map((l) => (
                      <span key={l} className="block">
                        {l}
                      </span>
                    ))}
                  </span>
                ))}
              </span>
            </li>
          </ul>
          <Link
            to="/contact"
            className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-gold transition-colors hover:text-gold-light"
            data-testid="footer-enquiry-link"
          >
            Submit a legal enquiry <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x flex flex-col items-start justify-between gap-3 py-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            © {year} Upgrade Law · India
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            Content is general information, not legal advice
          </p>
        </div>
      </div>
    </footer>
  );
};
